// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf editor's rig layer (frontend-aev.5, .6, .7): every block as
 * a BONE — a thick capsule following its own trajectory from its first to
 * its last camera, its cameras as dots — with a JOINT sphere at every split
 * inside a session (a detached joint is drawn hollow), the suggested splits
 * as small hollow rings, a three.js TransformControls gizmo on the
 * selection, the live rigid preview (the selected bones move with the
 * gizmo) and the server's blended preview (`preview: true`) as a dotted
 * overlay coloured by blend weight.
 *
 * Pure decisions (which bones a click selects, snapping, the delta) live in
 * `posegraf/rig.ts` and `posegraf/snap.ts`; this class only draws and picks.
 * Coordinates: world in, scene-local inside (`registerExternalBounds`), like
 * every other layer.
 */

import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';

import type { BlockSplit } from '../api/types';
import { sessionColorToken, type BlockKey, type Rig, type RigJoint, type RigSelection, type Vec3 } from '../posegraf/rig';
import type { GizmoDelta } from '../posegraf/snap';
import type { PointCloudScene } from './PointCloudScene';

const FALLBACK = '#8a8f99';
/** Capsule radius and joint sphere radius (m). */
const BONE_RADIUS = 0.14;
const JOINT_RADIUS = 0.42;
const SUGGESTION_RADIUS = 0.3;
/** Most path points a capsule follows (the trajectory is decimated to this). */
const MAX_PATH_POINTS = 64;
/** Most cameras per bone given to box-select's projection. */
const MAX_PICK_POINTS = 160;

export type RigHit =
  | { kind: 'bone'; key: BlockKey; node: number | null }
  | { kind: 'joint'; joint: RigJoint }
  | { kind: 'suggestion'; node: number };

export interface GizmoListener {
  start(): void;
  /** The raw (unsnapped) delta the gizmo is at now. */
  change(raw: GizmoDelta): void;
  end(): void;
}

interface BoneObjects {
  key: BlockKey;
  group: THREE.Group;
  tube: THREE.Mesh;
  caps: THREE.Mesh[];
  cameras: THREE.Points;
  nodes: number[];
  session: number;
  pickPoints: number[];
}

interface JointObjects {
  joint: RigJoint;
  mesh: THREE.Mesh;
}

/** Decimate a polyline to at most `max` points, keeping both ends. */
function decimate(points: readonly Vec3[], max: number): Vec3[] {
  if (points.length <= max) return [...points];
  const out: Vec3[] = [];
  const step = (points.length - 1) / (max - 1);
  for (let i = 0; i < max; i += 1) out.push(points[Math.round(i * step)]);
  return out;
}

export class RigScene {
  private readonly host: PointCloudScene;
  private readonly root = new THREE.Group();
  private readonly overlay = new THREE.Group();
  private bones: BoneObjects[] = [];
  private joints: JointObjects[] = [];
  private suggestionMeshes: { node: number; mesh: THREE.Mesh }[] = [];
  private blend: THREE.Points | null = null;
  private origin = new THREE.Vector3();
  private selection: RigSelection = new Set();
  private previewMatrix: THREE.Matrix4 | null = null;

  private readonly pivot = new THREE.Object3D();
  private pivotStart = new THREE.Vector3();
  private gizmo: TransformControls | null = null;
  private gizmoHelper: THREE.Object3D | null = null;
  private listener: GizmoListener | null = null;
  private offCamera: (() => void) | null = null;
  private dragging = false;

  private readonly materials = {
    selected: new THREE.MeshStandardMaterial({ roughness: 0.6, metalness: 0 }),
    joint: new THREE.MeshStandardMaterial({ roughness: 0.4, metalness: 0 }),
    detached: new THREE.MeshBasicMaterial({ wireframe: true }),
    suggestion: new THREE.MeshBasicMaterial({ wireframe: true, transparent: true, opacity: 0.9 }),
  };
  private sessionMaterials = new Map<number, THREE.MeshStandardMaterial>();
  private cameraMaterials = new Map<number, THREE.PointsMaterial>();

  constructor(host: PointCloudScene) {
    this.host = host;
    this.root.name = 'rig';
    this.overlay.name = 'rig-overlay';
    this.pivot.rotation.order = 'ZYX';
    host.sceneRoot().add(this.root, this.overlay, this.pivot);
    this.applyTheme();
  }

  /** Re-read the colour tokens (theme switch). */
  applyTheme(): void {
    const c = (token: string) => this.host.resolveColorToken(token, FALLBACK);
    // Selected bones: the brightest neutral on the near-black canvas (both
    // themes), unselected ones dimmed — the session hues stay readable.
    this.materials.selected.color.copy(c('--color-on-chrome'));
    this.materials.selected.emissive.copy(c('--color-on-chrome')).multiplyScalar(0.45);
    this.materials.joint.color.copy(c('--color-ghost'));
    this.materials.detached.color.copy(c('--color-status-failed'));
    this.materials.suggestion.color.copy(c('--label-3'));
    for (const [session, m] of this.sessionMaterials) m.color.copy(c(sessionColorToken(session)));
    for (const [session, m] of this.cameraMaterials) m.color.copy(c(sessionColorToken(session)));
  }

  private sessionMaterial(session: number): THREE.MeshStandardMaterial {
    let m = this.sessionMaterials.get(session);
    if (!m) {
      m = new THREE.MeshStandardMaterial({ roughness: 0.7, metalness: 0, transparent: true, opacity: 0.85 });
      m.color.copy(this.host.resolveColorToken(sessionColorToken(session), FALLBACK));
      this.sessionMaterials.set(session, m);
    }
    return m;
  }

  private cameraMaterial(session: number): THREE.PointsMaterial {
    let m = this.cameraMaterials.get(session);
    if (!m) {
      m = new THREE.PointsMaterial({ size: 4, sizeAttenuation: false });
      m.color.copy(this.host.resolveColorToken(sessionColorToken(session), FALLBACK));
      this.cameraMaterials.set(session, m);
    }
    return m;
  }

  private local(p: Vec3): THREE.Vector3 {
    return new THREE.Vector3(p[0] - this.origin.x, p[1] - this.origin.y, p[2] - this.origin.z);
  }

  private clearGroup(group: THREE.Group): void {
    for (const child of [...group.children]) {
      group.remove(child);
      child.traverse((o) => {
        const mesh = o as THREE.Mesh;
        mesh.geometry?.dispose();
      });
    }
  }

  /** Rebuild every bone, joint and suggestion. */
  setRig(rig: Rig, suggestions: readonly BlockSplit[], positions: ReadonlyMap<number, Vec3>): void {
    this.clearGroup(this.root);
    this.bones = [];
    this.joints = [];
    this.suggestionMeshes = [];
    const box = new THREE.Box3();
    for (const bone of rig.bones) for (const p of bone.positions) box.expandByPoint(new THREE.Vector3(...p));
    if (box.isEmpty()) return;
    this.origin.copy(this.host.registerExternalBounds(box));

    const sphere = new THREE.SphereGeometry(1, 16, 12);
    for (const bone of rig.bones) {
      const group = new THREE.Group();
      group.matrixAutoUpdate = false;
      const path = decimate(bone.positions, MAX_PATH_POINTS).map((p) => this.local(p));
      const material = this.sessionMaterial(bone.block.session);
      let tube: THREE.Mesh;
      const caps: THREE.Mesh[] = [];
      if (path.length >= 2) {
        const curve = new THREE.CatmullRomCurve3(path, false, 'centripetal');
        tube = new THREE.Mesh(new THREE.TubeGeometry(curve, Math.min(400, path.length * 4), BONE_RADIUS, 8, false), material);
        for (const end of [path[0], path[path.length - 1]]) {
          const cap = new THREE.Mesh(sphere.clone(), material);
          cap.scale.setScalar(BONE_RADIUS);
          cap.position.copy(end);
          caps.push(cap);
        }
      } else {
        tube = new THREE.Mesh(sphere.clone(), material);
        tube.scale.setScalar(BONE_RADIUS * 2);
        if (path[0]) tube.position.copy(path[0]);
      }
      tube.userData.boneKey = bone.key;
      for (const cap of caps) cap.userData.boneKey = bone.key;
      const camPos = new Float32Array(bone.positions.length * 3);
      bone.positions.forEach((p, i) => camPos.set([p[0] - this.origin.x, p[1] - this.origin.y, p[2] - this.origin.z], i * 3));
      const camGeom = new THREE.BufferGeometry();
      camGeom.setAttribute('position', new THREE.BufferAttribute(camPos, 3));
      const cameras = new THREE.Points(camGeom, this.cameraMaterial(bone.block.session));
      cameras.userData.boneKey = bone.key;
      group.add(tube, ...caps, cameras);
      this.root.add(group);
      const pickPoints = decimate(bone.positions, MAX_PICK_POINTS).flat();
      this.bones.push({ key: bone.key, group, tube, caps, cameras, nodes: bone.nodes, session: bone.block.session, pickPoints });
    }

    for (const joint of rig.joints) {
      if (!joint.position) continue;
      const mesh = new THREE.Mesh(sphere.clone(), joint.detached ? this.materials.detached : this.materials.joint);
      mesh.scale.setScalar(JOINT_RADIUS);
      mesh.position.copy(this.local(joint.position));
      mesh.userData.joint = joint;
      mesh.matrixAutoUpdate = true;
      const holder = new THREE.Group();
      holder.matrixAutoUpdate = false;
      holder.add(mesh);
      this.root.add(holder);
      this.joints.push({ joint, mesh });
    }

    for (const s of suggestions) {
      const p = positions.get(s.node_id);
      if (!p) continue;
      const mesh = new THREE.Mesh(new THREE.OctahedronGeometry(1, 0), this.materials.suggestion);
      mesh.scale.setScalar(SUGGESTION_RADIUS);
      mesh.position.copy(this.local(p));
      mesh.position.z += JOINT_RADIUS * 1.6;
      mesh.userData.suggestion = s.node_id;
      this.root.add(mesh);
      this.suggestionMeshes.push({ node: s.node_id, mesh });
    }
    sphere.dispose();
    this.applySelection();
    this.applyPreview();
  }

  setSelection(selection: RigSelection): void {
    this.selection = selection;
    this.applySelection();
    this.applyPreview();
  }

  setSuggestionsVisible(visible: boolean): void {
    for (const s of this.suggestionMeshes) s.mesh.visible = visible;
  }

  private applySelection(): void {
    const dim = this.selection.size > 0;
    for (const m of this.sessionMaterials.values()) m.opacity = dim ? 0.35 : 0.85;
    for (const b of this.bones) {
      const on = this.selection.has(b.key);
      const m = on ? this.materials.selected : this.sessionMaterial(b.session);
      b.tube.material = m;
      for (const cap of b.caps) cap.material = m;
      b.tube.renderOrder = on ? 2 : 0;
    }
  }

  /** The client-side live preview: a world-frame row-major delta for the selected bones; null = none. */
  setPreviewDelta(rowMajor: readonly number[] | null): void {
    if (!rowMajor) {
      this.previewMatrix = null;
    } else {
      const m = new THREE.Matrix4().set(...(rowMajor as [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number]));
      // Scene-local: T(-o) · M · T(o).
      const o = this.origin;
      this.previewMatrix = new THREE.Matrix4().makeTranslation(-o.x, -o.y, -o.z).multiply(m).multiply(new THREE.Matrix4().makeTranslation(o.x, o.y, o.z));
    }
    this.applyPreview();
  }

  private applyPreview(): void {
    const identity = new THREE.Matrix4();
    for (const b of this.bones) {
      b.group.matrix.copy(this.previewMatrix && this.selection.has(b.key) ? this.previewMatrix : identity);
      b.group.matrixWorldNeedsUpdate = true;
    }
    for (const j of this.joints) {
      const holder = j.mesh.parent as THREE.Group;
      holder.matrix.copy(this.previewMatrix && this.selection.has(j.joint.nextKey) ? this.previewMatrix : identity);
      holder.matrixWorldNeedsUpdate = true;
    }
  }

  /** The server's blended preview: every moved frame's new position and weight; null clears it. */
  setBlendPreview(frames: readonly { position: Vec3; weight: number }[] | null): void {
    if (this.blend) {
      this.overlay.remove(this.blend);
      this.blend.geometry.dispose();
      (this.blend.material as THREE.Material).dispose();
      this.blend = null;
    }
    if (!frames || frames.length === 0) return;
    const pos = new Float32Array(frames.length * 3);
    const col = new Float32Array(frames.length * 3);
    const full = this.host.resolveColorToken('--color-accent', FALLBACK);
    const zone = this.host.resolveColorToken('--label-0', FALLBACK);
    frames.forEach((f, i) => {
      pos.set([f.position[0] - this.origin.x, f.position[1] - this.origin.y, f.position[2] - this.origin.z], i * 3);
      const c = zone.clone().lerp(full, Math.min(1, Math.max(0, f.weight)));
      col.set([c.r, c.g, c.b], i * 3);
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    this.blend = new THREE.Points(g, new THREE.PointsMaterial({ size: 6, sizeAttenuation: false, vertexColors: true, depthTest: false }));
    this.blend.renderOrder = 5;
    this.overlay.add(this.blend);
  }

  /** What is under the ray: a joint first, then a suggestion, then a bone (with the nearest camera). */
  pick(raycaster: THREE.Raycaster): RigHit | null {
    const jointHit = raycaster.intersectObjects(this.joints.map((j) => j.mesh), false)[0];
    if (jointHit) return { kind: 'joint', joint: jointHit.object.userData.joint as RigJoint };
    const sugHit = raycaster.intersectObjects(this.suggestionMeshes.filter((s) => s.mesh.visible).map((s) => s.mesh), false)[0];
    if (sugHit) return { kind: 'suggestion', node: sugHit.object.userData.suggestion as number };
    const prev = raycaster.params.Points?.threshold ?? 1;
    raycaster.params.Points = { threshold: 0.12 };
    const targets = this.bones.flatMap((b) => [b.cameras, b.tube, ...b.caps]);
    const hits = raycaster.intersectObjects(targets, false);
    raycaster.params.Points = { threshold: prev };
    const camHit = hits.find((h) => h.object instanceof THREE.Points && h.index !== undefined);
    const any = camHit ?? hits[0];
    if (!any) return null;
    const key = any.object.userData.boneKey as BlockKey;
    const bone = this.bones.find((b) => b.key === key);
    let node: number | null = null;
    if (bone) {
      if (camHit && camHit.object === bone.cameras && camHit.index !== undefined) node = bone.nodes[camHit.index] ?? null;
      else node = this.nearestNode(bone, any.point);
    }
    return { kind: 'bone', key, node };
  }

  private nearestNode(bone: BoneObjects, localPoint: THREE.Vector3): number | null {
    const attr = bone.cameras.geometry.getAttribute('position');
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < attr.count; i += 1) {
      const d = (attr.getX(i) - localPoint.x) ** 2 + (attr.getY(i) - localPoint.y) ** 2 + (attr.getZ(i) - localPoint.z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best >= 0 ? (bone.nodes[best] ?? null) : null;
  }

  /** Each bone's (decimated) cameras on screen, for box-select. */
  projectBones(): Map<BlockKey, Float32Array> {
    const out = new Map<BlockKey, Float32Array>();
    for (const b of this.bones) out.set(b.key, this.host.projectToScreen(b.pickPoints, false));
    return out;
  }

  /** One world point on screen (the gizmo's pivot), or null off screen. */
  screenOf(world: Vec3): { x: number; y: number } | null {
    const xy = this.host.projectToScreen(world, false);
    return Number.isNaN(xy[0]) ? null : { x: xy[0], y: xy[1] };
  }

  // --------------------------------------------------------------- gizmo --

  /** Put the gizmo on `pivotWorld` (zero delta), or take it away (null). */
  attachGizmo(pivotWorld: Vec3 | null, listener: GizmoListener | null): void {
    this.listener = listener;
    if (!pivotWorld) {
      this.detachGizmo();
      return;
    }
    if (!this.gizmo) {
      const gizmo = new TransformControls(this.host.getCamera(), this.host.getCanvas());
      gizmo.setSize(0.9);
      gizmo.setSpace('world');
      gizmo.addEventListener('dragging-changed', (e) => {
        const on = Boolean((e as unknown as { value: boolean }).value);
        this.dragging = on;
        this.host.setControlsEnabled(!on);
        if (on) this.listener?.start();
        else this.listener?.end();
      });
      gizmo.addEventListener('objectChange', () => {
        if (this.dragging) this.listener?.change(this.rawDelta());
      });
      this.gizmo = gizmo;
      this.gizmoHelper = gizmo.getHelper();
      this.host.sceneRoot().add(this.gizmoHelper);
      this.offCamera = this.host.onCameraChange(() => {
        if (this.gizmo && this.gizmo.camera !== this.host.getCamera()) this.gizmo.camera = this.host.getCamera();
      });
      gizmo.attach(this.pivot);
      this.setGizmoMode('translate');
    }
    this.pivotStart.copy(this.local(pivotWorld));
    this.pivot.position.copy(this.pivotStart);
    this.pivot.rotation.set(0, 0, 0);
  }

  private detachGizmo(): void {
    if (!this.gizmo) return;
    this.gizmo.detach();
    if (this.gizmoHelper) this.host.sceneRoot().remove(this.gizmoHelper);
    this.gizmo.dispose();
    this.offCamera?.();
    this.offCamera = null;
    this.gizmo = null;
    this.gizmoHelper = null;
    this.host.setControlsEnabled(true);
  }

  /** Translate (all axes) or rotate (yaw only: the z ring). */
  setGizmoMode(mode: 'translate' | 'rotate'): void {
    if (!this.gizmo) return;
    this.gizmo.setMode(mode);
    const rotate = mode === 'rotate';
    this.gizmo.showX = !rotate;
    this.gizmo.showY = !rotate;
    this.gizmo.showZ = true;
  }

  isDragging(): boolean {
    return this.dragging;
  }

  private rawDelta(): GizmoDelta {
    const d = this.pivot.position.clone().sub(this.pivotStart);
    const deg = 180 / Math.PI;
    return { dx: d.x, dy: d.y, dz: d.z, yaw: this.pivot.rotation.z * deg, pitch: this.pivot.rotation.y * deg, roll: this.pivot.rotation.x * deg };
  }

  /** Show `d` on the gizmo (a snapped or typed delta). */
  setGizmoDelta(d: GizmoDelta): void {
    const rad = Math.PI / 180;
    this.pivot.position.set(this.pivotStart.x + d.dx, this.pivotStart.y + d.dy, this.pivotStart.z + d.dz);
    this.pivot.rotation.set(d.roll * rad, d.pitch * rad, d.yaw * rad, 'ZYX');
    this.pivot.updateMatrixWorld();
  }

  dispose(): void {
    this.detachGizmo();
    this.setBlendPreview(null);
    this.clearGroup(this.root);
    this.host.sceneRoot().remove(this.root, this.overlay, this.pivot);
    for (const m of Object.values(this.materials)) m.dispose();
    for (const m of this.sessionMaterials.values()) m.dispose();
    for (const m of this.cameraMaterials.values()) m.dispose();
  }
}
